import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard, Kpi } from "./SuperadminLayout";
import { toast } from "sonner";

function fmtUsd(microUsd) {
  const usd = (microUsd || 0) / 1_000_000;
  return usd.toLocaleString("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}
function fmtNum(n) { return (n || 0).toLocaleString("fr-FR"); }

export default function SuperadminLlm() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/llm-usage");
        setData(data);
      } catch (e) { toast.error("Chargement impossible"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <SuperadminLayout title="Coût IA (Emergent LLM Key)"><p className="text-[#8494A8]">Chargement…</p></SuperadminLayout>;
  if (!data) return <SuperadminLayout title="Coût IA (Emergent LLM Key)"><p className="text-red-400">Erreur</p></SuperadminLayout>;

  const maxDailyCost = Math.max(1, ...data.daily.map((d) => d.cost));

  return (
    <SuperadminLayout title="Coût IA — Emergent LLM Key">
      <p className="text-sm text-[#8494A8] mb-6 max-w-3xl">
        Consommation de tokens des trois pipelines IA : extraction de documents (Claude Sonnet), génération de questionnaires (Claude Sonnet) et traduction patient (Gemini Flash).
        Les tokens sont estimés à partir de la longueur des messages — l'écart moyen avec la facturation réelle Emergent est de 5-10 %.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Ce mois-ci (MTD)" value={fmtUsd(data.totals.mtd.cost_micro_usd)}
             sub={`${fmtNum(data.totals.mtd.calls)} appels · ${fmtNum(data.totals.mtd.input_tokens + data.totals.mtd.output_tokens)} tokens`}
             testid="llm-kpi-mtd" />
        <Kpi label="Cette année (YTD)" value={fmtUsd(data.totals.ytd.cost_micro_usd)}
             sub={`${fmtNum(data.totals.ytd.calls)} appels · ${fmtNum(data.totals.ytd.input_tokens + data.totals.ytd.output_tokens)} tokens`}
             testid="llm-kpi-ytd" />
        <Kpi label="Depuis toujours" value={fmtUsd(data.totals.all_time.cost_micro_usd)}
             sub={`${fmtNum(data.totals.all_time.calls)} appels`}
             testid="llm-kpi-all" />
      </div>

      <DarkCard className="p-6 mb-6" data-testid="llm-daily">
        <h2 className="text-lg font-semibold text-white mb-4">Coût journalier (30 derniers jours)</h2>
        <div className="flex items-end gap-1 h-40">
          {data.daily.length === 0 ? (
            <p className="text-sm text-[#8494A8]">Aucune consommation encore enregistrée</p>
          ) : (
            data.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col justify-end items-center group relative min-w-0">
                <div className="w-full bg-[#E4572E] rounded-t transition-all hover:bg-[#F27551]"
                     style={{ height: `${(d.cost / maxDailyCost) * 100}%`, minHeight: d.cost > 0 ? "3px" : "0" }} />
                <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 bg-black/80 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                  {d.day} · {fmtUsd(d.cost)} · {d.calls} appels
                </div>
              </div>
            ))
          )}
        </div>
      </DarkCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DarkCard className="overflow-hidden" data-testid="llm-by-model">
          <div className="px-5 py-3 border-b border-white/10">
            <h3 className="text-sm text-white font-semibold">Coût YTD par modèle</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[#8494A8] text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Modèle</th>
                <th className="text-right px-4 py-2 font-medium">Appels</th>
                <th className="text-right px-4 py-2 font-medium">Tokens</th>
                <th className="text-right px-4 py-2 font-medium">Coût</th>
              </tr>
            </thead>
            <tbody>
              {data.by_model.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-[#8494A8]">Aucune donnée</td></tr>}
              {data.by_model.map((r) => (
                <tr key={r.model} className="border-t border-white/5">
                  <td className="px-4 py-2 text-white font-mono text-xs">{r.model}</td>
                  <td className="px-4 py-2 text-right text-[#B7C4D6]">{fmtNum(r.calls)}</td>
                  <td className="px-4 py-2 text-right text-[#B7C4D6]">{fmtNum(r.input_tokens + r.output_tokens)}</td>
                  <td className="px-4 py-2 text-right text-white font-mono">{fmtUsd(r.cost_micro_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DarkCard>

        <DarkCard className="overflow-hidden" data-testid="llm-by-feature">
          <div className="px-5 py-3 border-b border-white/10">
            <h3 className="text-sm text-white font-semibold">Coût YTD par fonctionnalité</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[#8494A8] text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Feature</th>
                <th className="text-right px-4 py-2 font-medium">Appels</th>
                <th className="text-right px-4 py-2 font-medium">Coût</th>
              </tr>
            </thead>
            <tbody>
              {data.by_feature.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-[#8494A8]">Aucune donnée</td></tr>}
              {data.by_feature.map((r) => (
                <tr key={r.feature} className="border-t border-white/5">
                  <td className="px-4 py-2 text-white text-xs">{r.feature}</td>
                  <td className="px-4 py-2 text-right text-[#B7C4D6]">{fmtNum(r.calls)}</td>
                  <td className="px-4 py-2 text-right text-white font-mono">{fmtUsd(r.cost_micro_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DarkCard>
      </div>

      <DarkCard className="overflow-hidden" data-testid="llm-by-user">
        <div className="px-5 py-3 border-b border-white/10 flex justify-between items-baseline">
          <h3 className="text-sm text-white font-semibold">Top 15 utilisateurs (MTD)</h3>
          <span className="text-xs text-[#8494A8]">classés par coût décroissant</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8] text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Utilisateur</th>
              <th className="text-right px-4 py-2 font-medium">Appels</th>
              <th className="text-right px-4 py-2 font-medium">Input</th>
              <th className="text-right px-4 py-2 font-medium">Output</th>
              <th className="text-right px-4 py-2 font-medium">Coût MTD</th>
            </tr>
          </thead>
          <tbody>
            {data.by_user.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-[#8494A8]">Aucune activité IA ce mois-ci</td></tr>}
            {data.by_user.map((r) => (
              <tr key={r.user_id || r.user_email} className="border-t border-white/5">
                <td className="px-4 py-2 text-white text-xs break-all">{r.user_email}</td>
                <td className="px-4 py-2 text-right text-[#B7C4D6]">{fmtNum(r.calls)}</td>
                <td className="px-4 py-2 text-right text-[#B7C4D6] font-mono text-xs">{fmtNum(r.input_tokens)}</td>
                <td className="px-4 py-2 text-right text-[#B7C4D6] font-mono text-xs">{fmtNum(r.output_tokens)}</td>
                <td className="px-4 py-2 text-right text-white font-mono">{fmtUsd(r.cost_micro_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>

      <p className="text-xs text-[#8494A8] mt-6 text-right">
        Généré le {new Date(data.generated_at).toLocaleString("fr-FR")}
      </p>
    </SuperadminLayout>
  );
}
